import i18n from '@/i18n'
import { driver } from 'driver.js'
import 'driver.js/dist/driver.css'
import { defineStore } from 'pinia'
import { computed, reactive, ref } from 'vue'

export const useUserStore = defineStore('user', () => {
	const userInfo = reactive({
		name: '',
		isLogged: false,
		email: '',
		isLoaded: false,
		uid: '',
		type: '',
		isNationalAccountsUser: false,
		showDistrictLevel: false,
		availableDistricts: [],
		manageUserOptions: [],
		manageCustomerOptions: [],
	})
	const resetUser = reactive({
		password: '',
		email: '',
		oobCode: '',
		oobCodeValid: true,
	})
	const dialogInfo = reactive({
		message: '',
		title: '',
		buttonTitle: '',
		type: '',
		dialogOn: false,
	})
	const isSuperUser = ref(false)
	const exportRequest = ref('')
	const orgInfo = ref([])
	const orgLogo = ref('')
	const accountList = ref([])
	const spinnerOn = ref(false)
	const websiteURL = ref(
		process.env.VUE_APP_COUNTRY === 'CAN'
			? 'https://wcc-national-account-prod.firebaseapp.com'
			: 'https://large-account-portal-prod.web.app'
	)
	const countryVersion = process.env.VUE_APP_COUNTRY

	// Driver.js step tutorial thing
	const driverObj = driver({
		showProgress: true,
	})
	const driverSteps = ref([])

	// UX
	const interfaceState = reactive({
		// general
		loadingTab: false,
		loadingMain: false,
		drawer: false, // controls mobile menu

		// account
		yearSelected: new Date().getFullYear(),
		yearOptions: Array.from({ length: new Date().getFullYear() - 2017 }, (_, i) => new Date().getFullYear() - i),

		// org
		searchFieldType: 'Name',
		searchFieldText: '',
		showInactive: false,
		districtIdsFilter: [],
		allDistrictsChecked: false,
		lineOfBusinessFilter: '',
		recordTypeFilter: 'Customer',
		currentPage: 1,
		itemsPerPage: 15,
		sortBy: 'Name',
		prevSortBy: '',
		sortDirection: 'ASC',
		prevSortDirection: 'ASC',
		isCanada: true,
		prevRecordType: '',
		filterDialog: false,

		//security
		passedSecurity: false,
	})

	const userTypes = {
		'WCN Admin': {
			type: 'WCN Admin',
			permissions: {
				createUsers: true,
				editUsers: true,
				deleteUsers: true,
				viewAllAccounts: true,
				manageConnections: true,
				accessReports: true,
				manageDistricts: true,
				manageProvinces: true,
				manageNationalAccounts: true,
			},
		},
		'WCN National Account Admin': {
			type: 'WCN National Account Admin',
			permissions: {
				createUsers: true,
				editUsers: true,
				deleteUsers: false,
				viewAllAccounts: true,
				manageConnections: true,
				accessReports: true,
				manageNationalAccounts: true,
				manageDistricts: false,
				manageProvinces: false,
			},
		},
		'WCN National Account Manager': {
			type: 'WCN National Account Manager',
			permissions: {
				createUsers: true,
				editUsers: true,
				deleteUsers: false,
				viewAllAccounts: true,
				manageConnections: true,
				accessReports: true,
				manageNationalAccounts: true,
				manageDistricts: false,
				manageProvinces: false,
			},
		},
		'WCN District Manager': {
			type: 'WCN District Manager',
			permissions: {
				createUsers: true,
				editUsers: true,
				deleteUsers: false,
				viewDistrictAccounts: true,
				manageConnections: true,
				accessReports: true,
				manageDistricts: true,
				manageNationalAccounts: false,
				manageProvinces: false,
			},
		},
		'External Admin': {
			type: 'External Admin',
			isNationalAccountsUser: false,
			permissions: {
				createUsers: true,
				editUsers: true,
				deleteUsers: false,
				viewAssignedAccounts: true,
				manageConnections: true,
				accessReports: true,
				manageDistricts: false,
				manageNationalAccounts: false,
				manageProvinces: false,
			},
		},
		Broker: {
			type: 'Broker',
			permissions: {
				createUsers: false,
				editUsers: false,
				deleteUsers: false,
				viewAssignedAccounts: true,
				manageConnections: false,
				accessReports: true,
				manageDistricts: false,
				manageNationalAccounts: false,
				manageProvinces: false,
			},
		},
		External: {
			type: 'External',
			permissions: {
				createUsers: false,
				editUsers: false,
				deleteUsers: false,
				viewAssignedAccounts: true,
				manageConnections: false,
				accessReports: true,
				manageDistricts: false,
				manageNationalAccounts: false,
				manageProvinces: false,
			},
		},
	}

	const getHeadersByUserType = computed(() => {
		return (userType, isNationalAccountsUser = false) => {
			const baseHeaders = [
				{ text: i18n.t('name'), value: 'Name' },
				{ text: i18n.t('accountNumber'), value: 'AccountNumber' },
			]

			// For WCN National Account Manager and External Admin with National Accounts
			if (
				userType === 'WCN National Account Admin' ||
				userType === 'WCN National Account Manager' ||
				((userType === 'External' || userType === 'External Admin') && isNationalAccountsUser)
			) {
				return [
					...baseHeaders,
					{ text: 'District', value: 'Internal_District_Id__r.Name' },
					{ text: '', value: 'actions', sortable: false },
				]
			}

			// For WCN Admin - include district but no actions
			if (userType === 'WCN Admin') {
				return [...baseHeaders, { text: 'District', value: 'Internal_District_Id__r.Name' }]
			}

			// For WCN District Manager, Broker and regular External Admin - include district and actions
			if (
				userType === 'WCN District Manager' ||
				userType === 'Broker' ||
				((userType === 'External' || userType === 'External Admin') && !isNationalAccountsUser)
			) {
				return [
					...baseHeaders,
					{ text: 'District', value: 'Internal_District_Id__r.Name' },
					{ text: '', value: 'actions', sortable: false },
				]
			}

			// Default case
			return baseHeaders
		}
	})

	const userIsWCN = computed(() => {
		return (
			userInfo.type == 'WCN Admin' ||
			userInfo.type == 'WCN National Account Admin' ||
			userInfo.type == 'WCN National Account Manager' ||
			userInfo.type == 'WCN District Manager'
		)
	})

	const getUserPermissions = (userType, isNationalAccountsUser = false) => {
		const permissions = userTypes.value[userType]?.permissions || {}
		if (userType === 'External Admin' && isNationalAccountsUser) {
			return {
				...permissions,
				viewAllAccounts: true,
				manageNationalAccounts: true,
			}
		}
		return permissions
	}

	function modifyUserInfo(newUserInfo) {
		Object.assign(userInfo, newUserInfo)
	}

	function userLoggedOut() {
		Object.assign(userInfo, {
			name: '',
			isLogged: false,
			email: '',
			isLoaded: true,
			uid: '',
			type: '',
			isNationalAccountsUser: false,
			showDistrictLevel: false,
			availableDistricts: [],
			manageUserOptions: [],
			manageCustomerOptions: [],
		})
		accountList.value = []
	}

	function modifySuperUserInfo(newValue) {
		isSuperUser.value = newValue
	}

	function modifyDialogInfo(payload) {
		// Directly modify the reactive object properties
		dialogInfo.message = payload.message
		dialogInfo.title = payload.title
		dialogInfo.buttonTitle = payload.buttonTitle
		dialogInfo.type = payload.type
		dialogInfo.dialogOn = true
	}

	function closeDialogBox() {
		dialogInfo.dialogOn = false
	}

	function modifySpinnerStatus(payload) {
		spinnerOn.value = payload
	}

	function modifyOrgInfo(payload) {
		orgInfo.value = payload
	}

	function modifyAccountList(payload) {
		accountList.value = payload
	}

	function modifyOrgLogo(payload) {
		orgLogo.value = payload
	}

	function modifyExportRequest(payload) {
		exportRequest.value = payload
	}

	function setManageCustomerOptions() {
		userInfo.manageCustomerOptions = [
			{ title: i18n.t('customerGroups'), link: '/manage-customer-groups' },
			{ title: i18n.t('userAccess'), link: '/manage-users-access' },
		]
	}

	function destroyDriver() {
		driverSteps.value = []
		driverObj.destroy()
	}

	function showDriver() {
		interfaceState.drawer = false
		driverObj.drive()
	}

	function swapSearchField() {
		interfaceState.searchFieldType == 'AccountNumber'
			? (interfaceState.searchFieldType = 'Name')
			: (interfaceState.searchFieldType = 'AccountNumber')

		interfaceState.searchFieldText = ''
	}

	function resetInterfaceOrg() {
		interfaceState.searchFieldType = 'Name'
		interfaceState.searchFieldText = ''
		interfaceState.showInactive = false
		interfaceState.districtIdsFilter = []
		interfaceState.allDistrictsChecked = false
		interfaceState.lineOfBusinessFilter = ''
		//interfaceState.recordTypeFilter = 'Customer'
		interfaceState.currentPage = 1
		interfaceState.itemsPerPage = 15
		interfaceState.sortBy = 'Name'
		interfaceState.prevSortBy = ''
		interfaceState.sortDirection = 'ASC'
		interfaceState.prevSortDirection = 'ASC'
		interfaceState.isCanada = true
		interfaceState.prevRecordType = ''
		interfaceState.filterDialog = false
	}

	function scrollTop() {
		window.scrollTo({ top: 0, behavior: 'smooth' })
	}

	return {
		userInfo,
		isSuperUser,
		dialogInfo,
		exportRequest,
		orgInfo,
		orgLogo,
		accountList,
		spinnerOn,
		websiteURL,
		countryVersion,
		driverObj,
		driverSteps,
		interfaceState,
		userTypes,
		getHeadersByUserType,
		userIsWCN,
		resetUser,
		getUserPermissions,
		modifyUserInfo,
		userLoggedOut,
		modifySuperUserInfo,
		modifyDialogInfo,
		closeDialogBox,
		modifySpinnerStatus,
		modifyOrgInfo,
		modifyAccountList,
		modifyOrgLogo,
		modifyExportRequest,
		setManageCustomerOptions,
		swapSearchField,
		destroyDriver,
		showDriver,
		resetInterfaceOrg,
		scrollTop,
	}
})
